import { UIElement } from '../ui-element.js';
import styles from './ui-overlay-controller.css';
import {
    appendModalControllerIfRequired,
    waitForAnimations,
} from '../../global/helpers.js';
import {
    mainMenuOverlayHandler,
    modalOverlayHandler,
    handleMenuOnResize,
    handleTooltipsOnResize,
    hasLastOverlay,
    OverlayWatchEvents,
} from '../../global/overlay.js';
import { browser, eventBus } from '../../global/index.js';
import {
    MenuOverlayCSSClass,
    TopModalCSSClass,
} from '../../global/constants.js';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIOverlayController
 * @element ui-overlay-controller
 * @classdesc Represents a class for <code>ui-overlay-controller</code> element.
 * @property {boolean} [opened] {@attr opened}
 * @property {string} [mainMenuSelector] {@attr main-menu-selector}
 * @property {UIModalController} modalController Shortcut to ui-modal-controller.
 * @property {UINav | Element | null} mainMenu Shortcut to ui-nav main menu.
 * @property {HTMLElement} root root Shortcut to root element.
 * @property {UIModal | undefined} lastModal Contains a last modal if exists.
 * @slot
 * @example
 * <ui-overlay-controller"></ui-overlay-controller>
 */
class UIOverlayController extends UIElement {
    static get props() {
        return {
            attributes: {
                opened: Boolean,
                mainMenuSelector: {
                    type: String,
                    default: '#nav-main',
                },
            },
        };
    }

    /**
     * @type {{CloseEnter: string, OpenEnter: string}}
     */
    static get TriggerAction() {
        return {
            CloseEnter: 'modal-close-enter',
            OpenEnter: 'modal-open-enter',
        };
    }

    get modalController() {
        return document.querySelector('ui-modal-controller');
    }

    get root() {
        return document.documentElement;
    }

    /**
     * @returns {UINav | Element | null}
     */
    get mainMenu() {
        return document.querySelector(this.mainMenuSelector);
    }

    /**
     * Handles different cases when overlay should be shown.
     * @param {CustomEvent | Event} event
     */
    handleOverlay(event) {
        /** @type {UIModal|HTMLElement} */
        const modal = event.target;
        const isMenuActive =
            this.mainMenu?.isActiveMobileMode() ||
            this.mainMenu?.isActiveDesktopMode();

        // 1. If menu is opened / closed
        const openCallback = () => {
            this.opened = true;
            this.root.classList.add(MenuOverlayCSSClass);
        };
        const closeCallback = async () => {
            try {
                await waitForAnimations(modal);
            } catch (error) {
                // animation interrupted, do nothing
            }
            this.root.classList.remove(MenuOverlayCSSClass);
            this.opened = false;
        };
        mainMenuOverlayHandler(this, event, openCallback, closeCallback);

        // 2. Modals are opened as fullscreen and popovers.
        const enterCallback = () => {
            requestAnimationFrame(this.markTopModal.bind(this, event.type));
            this.opened = true;
            this.root.classList.remove(MenuOverlayCSSClass);
        };
        const exitCallback = async () => {
            requestAnimationFrame(this.markTopModal.bind(this, event.type));
            try {
                await waitForAnimations(modal);
            } catch (error) {
                // animation interrupted, do nothing
            }
            this.opened = hasLastOverlay(this.modalController);
            if (isMenuActive) {
                this.root.classList.add(MenuOverlayCSSClass);
            }
        };
        const updateCallback = async () => {
            const action =
                event.detail.action === 'add'
                    ? this.constructor.TriggerAction.OpenEnter
                    : this.constructor.TriggerAction.CloseEnter;
            requestAnimationFrame(this.markTopModal.bind(this, action));
            try {
                await waitForAnimations(modal);
            } catch (error) {
                // animation interrupted, do nothing
            }
            this.opened = hasLastOverlay(this.modalController);
        };
        modalOverlayHandler(
            this,
            event,
            enterCallback,
            exitCallback,
            updateCallback
        );
    }

    /**
     * @param {('modal-open-enter' | 'modal-close-enter')} action
     * @private
     */
    markTopModal(action) {
        const modals = this.modalController.sortModalsByOrder(
            this.modalController.getActiveFullViewModals()
        );
        if (modals.length > 0) {
            modals.forEach((modal) => modal.classList.remove(TopModalCSSClass));
            let modal = modals.pop();
            if (!modal) {
                return;
            }
            if (typeof this.lastModal === 'undefined') {
                this.lastModal = modal;
            }
            if (
                action === this.constructor.TriggerAction.CloseEnter &&
                modal.isAnimated() &&
                this.lastModal?.isAnimated() &&
                modal !== this.lastModal &&
                !modal.opened
            ) {
                modal = modals.pop();
            }
            modal?.classList.add(TopModalCSSClass);
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        appendModalControllerIfRequired();
        this.overlayHandler = this.handleOverlay.bind(this);

        /** @type {UINav} */
        if (!this.root) {
            this.constructor.DEBUG_MODE &&
                console.error(
                    `${this.constructor.name}: root element ${this.root} doesn't exists!`
                );
            return;
        }
        OverlayWatchEvents.forEach((name) => {
            eventBus.addEventListener(name, this.overlayHandler);
        });
        this.mainMenu?.mobileMediaQueryList.addListener((event) => {
            const handleMenuOnResizeCallback = () => {
                this.opened = false;
                this.root.classList.remove(MenuOverlayCSSClass);
            };
            handleMenuOnResize(this, event, handleMenuOnResizeCallback);
        });
        browser.mobileMedia.addListener((event) => {
            const handleTooltipsOnResizeCallback = (args) => {
                this.opened = !!args.isMobileMediaMatches;
                const action = this.opened
                    ? this.constructor.TriggerAction.OpenEnter
                    : this.constructor.TriggerAction.CloseEnter;
                this.markTopModal(action);
            };
            handleTooltipsOnResize(this, event, handleTooltipsOnResizeCallback);
        });
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        OverlayWatchEvents.forEach((name) => {
            eventBus.removeEventListener(name, this.overlayHandler);
        });
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        OverlayWatchEvents.forEach((name) => {
            eventBus.addEventListener(name, this.overlayHandler);
        });
    }
}

UIOverlayController.defineElement('ui-overlay-controller', styles);

export { UIOverlayController };
